import React, { useContext, useState } from 'react';
import { VehicleInfoView } from '../../../../interfaces/responder.interface';
import "./VehicleDocuemnts.scss";
import { Document } from "../../../../interfaces/tester.interface";
import { AttachmentGetRequest } from '../../../../services/AttachmentGetRequest';
import { useNavigate } from 'react-router-dom';
import {
  UserAuthContext,
  Authorize,
} from "../../appLoginComponent/UserAuthContext";
import NotAuthorized from '../../appLoginComponent/NotAuthorized';
import { FileEarmarkText } from 'react-bootstrap-icons';
import Spinner from '../../../components/Spinner';

const DOCUMENT_CONFIG: Record<string, { permission: string; label: string; btnLabel: string }> = {
  FIREFIGHTING: {
    permission: 'AV_Document_Firefight',
    label: 'FIREFIGHTING',
    btnLabel: 'FIREFIGHTING CONSIDERATION'
  },
  TOWING: {
    permission: 'AV_Document_Towing',
    label: 'TOWING',
    btnLabel: 'TOWING CONSIDERATION'
  },
  SECURE: {
    permission: 'AV_Document_Securing',
    label: 'SECURE',
    btnLabel: 'HOW TO SECURE A VEHICLE'
  },
  EXTRICATION: {
    permission: 'AV_Document_Extrication',
    label: 'EXTRICATION',
    btnLabel: 'EXTRICATION CONSIDERATION'
  },
  POSTINCIDENT: {
    permission: 'AV_Document_Incident',
    label: 'POSTINCIDENT',
    btnLabel: 'POST-INCIDENT CONSIDERATION'
  }
};

interface VehicleDocumentsInfoProps {
  vehicleDocumentDetails: VehicleInfoView | null;
}

const VehicleDocumentsInfo: React.FC<VehicleDocumentsInfoProps> = ({ vehicleDocumentDetails }) => {
  let { isAuthorized } = useContext(
    UserAuthContext
  ) as Authorize;

  let navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const getDocumentLabels = (doc: any) => {
    const config = DOCUMENT_CONFIG[doc.documentTypeId];

    if (config) {
      return {
        documentLabel: config.label,
        documentBtnLabel: config.btnLabel,
        permission: config.permission
      };
    }

    return {
      documentLabel: doc.documentTitle,
      documentBtnLabel: doc.documentTitle,
      permission: null
    };
  };

  return (
    <div>
      {isAuthorized("AV_Vehicle_Search") ? (
        <div className="info-container">
          {vehicleDocumentDetails ? (
            <>
              {vehicleDocumentDetails.vehicle.documents && vehicleDocumentDetails.vehicle.documents.length > 0 ? (
                <div className="documents-section">
                  <div className="documents-section-header">VIEW DOCUMENTS</div>
                  <div className="documents-list">
                    {vehicleDocumentDetails.vehicle.documents
                        .slice()
                        .sort((a: any, b: any) => {
                          const order = ['SECURE', 'TOWING', 'EXTRICATION', 'FIREFIGHTING', 'POSTINCIDENT'];
                          const indexA = order.indexOf(a.documentTypeId);
                          const indexB = order.indexOf(b.documentTypeId);
                          
                          // If both are in the order array, sort by their position
                          if (indexA !== -1 && indexB !== -1) {
                            return indexA - indexB;
                          }
                          // If only A is in the order array, A comes first
                          if (indexA !== -1) return -1;
                          // If only B is in the order array, B comes first
                          if (indexB !== -1) return 1;
                          // If neither is in the order array, maintain original order
                          return 0;
                        })
                        .map((doc: any) => {
                      if (doc.documentTypeId === 'REG') {
                        //DON'T SHOW THE REGISTRATION DOCUMENT
                        return null;
                      }

                      const { documentBtnLabel, permission } = getDocumentLabels(doc);

                      // Check authorization if permission is required
                      if (permission && !isAuthorized(permission)) {
                        return null;
                      }

                      return (
                        <div key={doc.documentId} className="document-button-wrapper">
                          <button
                            className="document-type"
                            style={{ boxShadow: 'none', borderColor: 'transparent' }}
                            disabled={isLoading}
                            onClick={async () => {
                              setIsLoading(true);
                              const docSrc = (await getSrc(doc)).valueOf() as string;
                              navigate("/docViewer", { state: { docViewerSrc: docSrc, fromTabIndex: 1, documentLabel: documentBtnLabel, document: doc } })
                            }}
                          >
                            <FileEarmarkText size={20} />
                            {documentBtnLabel || doc.documentTitle}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="no-data-message">
                  No documents available for this vehicle.
                </div>
              )}
            </>
          ) : (
            <div className="no-data-message">
              No vehicle data available. Please search for a vehicle first.
            </div>
          )}
          {isLoading && <Spinner />}
        </div>
      ) : (
        <NotAuthorized />
      )}
    </div>
  );
};

async function getSrc(responderDocument: Document) {
  const request = new AttachmentGetRequest(responderDocument);
  const blob = await request.send();

  if (!(blob instanceof Blob)) {
    return false;
  }
  const urlObject = URL.createObjectURL(blob);
  return urlObject;
}

export default VehicleDocumentsInfo;